import React, { useEffect, useState } from 'react';
import {
  Paper,
  List,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Typography,
  Chip,
  Box,
  TextField
} from '@mui/material';
import { ignoredUrls } from './config/TextMessage';
import { CheckBoxSx } from './CheckBoxSx';
import CommonModal from './config/CommonModal';
import { MassFilterModal } from './MassFilterModal';
import { RootState } from 'src/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import {
  setFilterChecklistValues,
  setIsCheckBoxedSaved
} from 'src/redux/dashboard';
/**
 * Return HTML for list of checkBox
 *
 * @param param0 contain is {url, selected url value }
 *
 * @returns HTML string
 */
const CheckboxList = ({
  data,
  selectedValues,
  newData = [],
  skipData = [],
  isOnBoarding = false,
  difference = null,
  setMassFilterSnackBarOpen = null,
  isTransferSites = false
}) => {
  const isTransferMode = isTransferSites && Array.isArray(data);
  const normalizedData = isTransferMode ? data?.map((item) => item.url) : data;
  const valueMap = isTransferMode
    ? Object.fromEntries(data?.map((item) => [item?.url, item]))
    : null;

  let filterChecklistValues: any =
    useSelector((state: RootState) => state.dashboard.filterChecklistValues) ||
    [];

  const [checklistValues, setChecklistValues] = useState(() => {
    if (filterChecklistValues && filterChecklistValues.length > 0) {
      return filterChecklistValues;
    }
    if (!data || data.length === 0 || isTransferSites) {
      return [];
    }
    return data.filter(
      (url: any) =>
        ignoredUrls?.some((keyword: any) => url?.includes(keyword)) ||
        skipData?.some((skipUrl: any) => url === skipUrl)
    );
  });
  const [filteredURL, setFilteredURL] = useState(normalizedData || []);
  const [pagesURL, setPagesURL] = useState(normalizedData);
  const [openModal, setOpenModal] = useState(null);
  const dispatch = useDispatch();

  // Handel check box change event
  const handleChange = (event: any) => {
    const value = event.target.value;

    // Case 1: Transfer Site Mode → value is URL, store the object
    const actualValue = isTransferMode ? valueMap[value] : value;

    const isChecked = event.target.checked;

    if (isChecked) {
      const updated = [...checklistValues, actualValue];
      setChecklistValues(updated);
      dispatch(setFilterChecklistValues(updated));
    } else {
      const updated = checklistValues.filter((item) =>
        isTransferMode ? item.url !== value : item !== value
      );
      setChecklistValues(updated);
      dispatch(setFilterChecklistValues(updated));
    }
  };

  // Set initial selected values based on the condition
  useEffect(() => {
    if (data?.length == 0 || isTransferSites) return;

    const initialSelectedValues: any = data?.filter(
      (url: any) =>
        ignoredUrls?.some((keyword: any) => url?.includes(keyword)) ||
        skipData?.some((skipUrl: any) => url === skipUrl)
    );
    if (filterChecklistValues?.length == 0) {
      setChecklistValues(initialSelectedValues);
      dispatch(setFilterChecklistValues(initialSelectedValues));
    }
    setFilteredURL(data);
    // Remove error of page scan limit
    setPagesURL(data);
  }, [data]);

  // Send to child to parent selected value
  useEffect(() => {
    (async () => {
      selectedValues(checklistValues);
      dispatch(setIsCheckBoxedSaved(true));
    })();
    return () => { };
  }, [checklistValues]);

  // Handle filter search
  const handleInputChange = (e) => {
    // Extract search term from input field
    const searchTerm = e.target.value;

    // Create a new array with URLs that match the search term
    const filteredItems = pagesURL?.filter((url: string) =>
      url?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );

    // Check if the search term is empty
    if (searchTerm) {
      // If not empty, update the filteredURL state with the filteredItems array
      setFilteredURL(filteredItems);
    } else {
      // If empty, update the filteredURL state with the pagesURL array
      setFilteredURL(pagesURL);
    }
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(null);
  };

  return (
    <React.Fragment>
      <Box className="mb-1" sx={CheckBoxSx?.searchFilterBoxSx}>
        <TextField
          variant="outlined"
          key={'url-filter'}
          //InputProps={{ disableUnderline: true }}
          id="url-filter"
          //label="Search for Pages"
          placeholder="Search for Pages"
          type="text"
          size="small"
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '6px'
            }
          }}
          onChange={handleInputChange}
          InputProps={{
            startAdornment: (
              <img
                src={
                  process.env.PUBLIC_URL +
                  `/static/images/icons/search-icon-grey.svg`
                }
                alt="Icon"
                style={CheckBoxSx?.searchIconSx}
              />
            )
          }}
        />
        {!isTransferSites && (
          <Box
            onClick={() => {
              handleOpenModal();
            }}
            sx={CheckBoxSx?.filterBoxSx}
          >
            <img
              draggable={false}
              src={
                process.env.PUBLIC_URL + `/static/images/icons/filter-add.svg`
              }
              alt="Check Icon"
            />
          </Box>
        )}
      </Box>
      <Paper
        sx={{
          maxHeight:
            normalizedData?.length > 50 && !isOnBoarding
              ? 150
              : normalizedData?.length > 5 && isOnBoarding
                ? 250
                : 'auto',
          overflowX: 'hidden',
          height: '250px',
          overflowY: 'auto',
          boxShadow: 'none',
          '&::-webkit-scrollbar': {
            width: '3px'
          },
          '&::-webkit-scrollbar-track': {
            background: 'var(--color-bg-grey3)' // You can customize the track background color here
          },
          '&::-webkit-scrollbar-thumb': {
            background: 'var(--color-text-grey3)' // You can customize the thumb color here
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: 'var(--color-text-medium-grey)' // Customize thumb color on hover here
          }
        }}
        className="pl-2"
      >
        <List>
          <FormGroup>
            {filteredURL?.map((checkboxLabel, index) => (
              <FormControlLabel
                key={index}
                control={
                  <Checkbox
                    size="small"
                    onChange={handleChange}
                    //value={checkboxLabel}
                    value={checkboxLabel} // always URL in transfer mode
                    //checked={checklistValues?.includes(checkboxLabel)}
                    checked={
                      isTransferMode
                        ? checklistValues?.some(
                          (item) => item.url === checkboxLabel
                        )
                        : checklistValues?.includes(checkboxLabel)
                    }
                  />
                }
                label={
                  <React.Fragment>
                    <Box style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        sx={{
                          fontFamily: 'Inter',
                          fontSize: 'var(--font-size-14)',
                          fontWeight: 400,
                          lineHeight: 'var(--line-height-22)',
                          letterSpacing: '0em',
                          textAlign: 'left',
                          color: 'var(--color-primary)',
                          maxWidth: '29rem',
                          whiteSpace: 'normal', // Allows wrapping
                          wordBreak: 'break-all', // Breaks long words or URLs
                          overflowWrap: 'break-word' // Fallback for better wrapping
                        }}
                      >
                        {checkboxLabel}
                      </Typography>

                      {newData?.includes(checkboxLabel) && (
                        <Chip
                          label="New"
                          style={{
                            width: '47px',
                            height: '18px',
                            fontSize: '10px', // Set the desired font size
                            color: 'black', // Set the text color
                            backgroundColor: 'rgba(92, 255, 255, 0.3)', // Set the background color
                            marginLeft: '8px'
                          }}
                        />
                      )}
                      {skipData?.includes(checkboxLabel) && (
                        <Chip
                          label="Skipped"
                          style={{
                            width: 'auto',
                            height: '18px',
                            fontSize: '10px', // Set the desired font size
                            color: 'black', // Set the text color
                            backgroundColor: 'rgba(92, 255, 255, 0.3)', // Set the background color
                            marginLeft: '8px'
                          }}
                        />
                      )}
                    </Box>
                  </React.Fragment>
                }
              />
            ))}
          </FormGroup>
        </List>
      </Paper>
      <CommonModal
        open={openModal}
        handCloseModal={handleCloseModal}
        title={''}
        closebtn={false}
        isMassFilter={true}
      >
        <MassFilterModal
          handCloseModal={handleCloseModal}
          filteredURL={filteredURL}
          setChecklistValues={setChecklistValues}
          isOnBoarding={isOnBoarding}
          difference={difference}
          setMassFilterSnackBarOpen={setMassFilterSnackBarOpen}
        />
      </CommonModal>
    </React.Fragment>
  );
};

export default CheckboxList;
